// @ts-nocheck

import React, { useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../../../client';
import CurrencyExposant from '../CurrencyExposant';

import Gauge from './Gauge';
import MinusPlusBtn from './GridsStakeInput';

const WrapStake = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  border-radius: 1.2em;
  ${({ theme, $noPadding }) =>
    Target.__MOBILE__
      ? css`
          border: solid 2px ${theme.grids.colors.black};
          margin: 1em 0;
          padding: ${$noPadding ? '0.2em 0.2em 0.2em 0.6em' : '0.6em'};
        `
      : css`
          border: solid 2px ${theme.grids.colors.middlegrey2};
          margin: 0 0 1em 0;
          padding: 0 1em;
        `}
`;

const YourStake = styled.span`
  color: ${({ theme }) => theme.grids.colors.middlegrey3};
  display: flex;
  align-items: center;
`;

const Stake = styled(CurrencyExposant)`
  font-size: 1.4em;
  color: ${({ theme }) => theme.grids.colors.redlight};
`;

const Input = styled.input`
  border: none;
  border-radius: 0;
  padding: 0.3125em;
  text-align: right;
  width: 3.75em;
  box-sizing: border-box;
  background: ${({ theme }) => theme.grids.colors.black};
  color: #fff;
  font-family: BettingPoliticaExtraBold, sans-serif;

  &:focus {
    outline: none;
  }
`;

type GridsBasketStakeSelectorProps = {
  className?: string;
  stake: number;
  stakeLabel: string;
  maxStake: number;
  setStake: (stake: number) => void;
  isKeyboarDisabled?: boolean;
};

const GridsBasketStakeSelector = ({
  stakeLabel,
  stake,
  setStake,
  maxStake,
  className,
  isKeyboarDisabled = false,
}: GridsBasketStakeSelectorProps) => {
  const changeStake = useCallback(
    (newStake) => {
      const formattedStake = parseInt(newStake, 10);
      if (formattedStake !== stake && formattedStake > 0 && formattedStake <= maxStake) {
        setStake(formattedStake);
      }
    },
    [maxStake, setStake, stake],
  );

  const handleStakeDecrement = useCallback(() => {
    if (stake > 1) {
      setStake(stake - 1);
    }
  }, [setStake, stake]);

  const handleStakeIncrement = useCallback(() => {
    if (stake < maxStake) {
      setStake(stake + 1);
    }
  }, [maxStake, setStake, stake]);

  const handleStakeChangeFromKeyboard = useCallback(
    (e) => {
      if (typeof e === 'object' && 'target' in e) {
        changeStake(e.target.value);
      } else {
        changeStake(e);
      }
    },
    [changeStake],
  );

  const selectOnClick = useCallback((e) => {
    e.target.select();
  }, []);

  useEffect(() => {
    if (stake > maxStake) {
      setStake(maxStake);
    } else if (maxStake > 0 && stake === 0) {
      setStake(1);
    }
  }, [maxStake, setStake, stake]);

  return maxStake > 0 ? (
    <>
      <WrapStake className={className} $noPadding={Target.__MOBILE__ && maxStake > 1}>
        <YourStake>{stakeLabel}</YourStake>
        {maxStake > 1 ? (
          <>
            {Target.__MOBILE__ ? (
              <MinusPlusBtn
                onClickMinus={handleStakeDecrement}
                onClickPlus={handleStakeIncrement}
                onChange={changeStake}
                stake={stake ? stake?.toString() : ''}
                isKeyboarDisabled={isKeyboarDisabled}
              />
            ) : (
              <Input
                type="text"
                onChange={handleStakeChangeFromKeyboard}
                onClick={selectOnClick}
                value={stake ? stake?.toString() : ''}
              />
            )}
          </>
        ) : (
          <Stake amount={stake} />
        )}
      </WrapStake>
      <Gauge min={1} max={maxStake} value={stake} onChange={changeStake} isDisabled={maxStake === 1} />
    </>
  ) : null;
};

export default GridsBasketStakeSelector;
